import { Variables } from '@catapultsports/referee-react';
import { AePeriod } from 'activity-editor/context';
import { truncate } from 'lodash';
import { MouseState } from './graph-helpers';
import { renderToStaticMarkup } from 'react-dom/server';
import { ReactElement } from 'react-markdown';

// Constants
const LABEL_FONT_SIZE = 14;
const ATHLETE_ICON_WIDTH = 16;
const LABEL_PADDING = 30;
const ATHLETE_LABEL_PADDING = 40;
export const ZOOM_PADDING_MS = 20000;
export const PERIOD_BAR_BUFFER = 0.009;

// Highcharts not supported Html Keys
export const HIGHCHARTS_UNSUPPORTED_HTML_KEYS = [
    'viewBox',
    'xmlns',
    'title',
    'fill-rule',
    'clip-rule',
    'stroke-linejoin',
];

//Interfaces
export interface CustomPoint extends Highcharts.Point {
    custom: {
        athleteCount: number;
        periodId: string;
        duration: string;
    };
    plotY: number;
    shapeArgs: {
        x: number;
        y: number;
        width: number;
    };
}
export interface ExtendedEventTarget extends EventTarget {
    options: { name: string };
}
export interface TimelineMouseState
    extends Omit<MouseState, 'activeSide' | 'isWithinBuffer' | 'hasCreatedOffRotation' | 'activeMetadata'> {
    activeSide?: 'start' | 'end' | 'center';
    activeMetadata?: Highcharts.GanttPointOptionsObject;
}

const sortPeriods = (periods: AePeriod[]) => {
    return periods.sort((a: AePeriod, b: AePeriod) => {
        const athleteDiff = b.athleteCount - a.athleteCount;
        if (athleteDiff !== 0) {
            return athleteDiff;
        }

        const startTimeDiff = a.startDateTime.valueOf() - b.startDateTime.valueOf();
        if (startTimeDiff !== 0) {
            return startTimeDiff;
        }

        const durationA = a.endDateTime.valueOf() - a.startDateTime.valueOf();
        const durationB = b.endDateTime.valueOf() - b.startDateTime.valueOf();
        return durationB - durationA;
    });
};

const groupPeriods = (periods: AePeriod[]) => {
    // Array to store the grouped periods
    const groupedPeriods: AePeriod[][] = [];

    // Array to store the sorted periods
    let resultSortedPeriods = periods;
    while (resultSortedPeriods.length > 0) {
        const groupedPeriodRow: AePeriod[] = [resultSortedPeriods[0]];
        for (let i = 1; i < resultSortedPeriods.length; i++) {
            const currentPeriod = resultSortedPeriods[i];
            let overlaps = false;

            for (const finalPeriod of groupedPeriodRow) {
                if (
                    currentPeriod.startDateTime < finalPeriod.endDateTime &&
                    currentPeriod.endDateTime > finalPeriod.startDateTime
                ) {
                    overlaps = true;
                    break;
                }
            }

            if (!overlaps) {
                groupedPeriodRow.push(currentPeriod);
            }
        }

        const sortedPeriodRow = groupedPeriodRow.sort((a, b) => a.startDateTime.valueOf() - b.startDateTime.valueOf());
        // Remove the periods that were included in result from resultSortedPeriods
        resultSortedPeriods = resultSortedPeriods.filter((period) => !sortedPeriodRow.includes(period));
        groupedPeriods.push(groupedPeriodRow);
    }
    return groupedPeriods;
};

// sorts and group periods and return a new array
export const arrangePeriods = (p: AePeriod[]) => groupPeriods(sortPeriods([...p]));

// Calculate the width of a string in pixels
export const getStringWidth = (str: string, fontSize: number) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context) {
        context.font = `${fontSize}px 'Mark Pro', '${Variables.css?.fontBody}'`; // TODO: Remove hardcoded font
        return context.measureText(str).width;
    }

    return 0;
};

// Calculate the width of a Gantt bar
export const getBarWidth = (startingPoint: number, endingPoint: number, chart: Highcharts.Chart) => {
    const plotStartPixel = chart.xAxis[0].toPixels(startingPoint, true);
    const plotEndPixel = chart.xAxis[0].toPixels(endingPoint, true);
    return plotEndPixel - plotStartPixel;
};

// Adjust the label text based on the bar width
export const adjustLabelText = (barWidth: number, textWidth: number, labelName: string, charWidth: number) => {
    barWidth = barWidth - 20; //10px from each side

    if (textWidth && textWidth > barWidth) {
        const maxLength = Math.floor(barWidth / charWidth);
        return truncate(labelName, { length: maxLength });
    }

    return labelName;
};

export const formatLabel = (name: string, athleteCount: number, barWidth: number) => {
    let truncatedString = name ?? '';
    let isAthleteContainerVisible = true;

    let textWidth = getStringWidth(name, LABEL_FONT_SIZE);
    const athleteCountWidth = getStringWidth(`${athleteCount}`, LABEL_FONT_SIZE);
    // truncate levels
    // 1st level: Remove the icon and number of athletes.
    if (barWidth < textWidth + LABEL_PADDING + ATHLETE_ICON_WIDTH + ATHLETE_LABEL_PADDING + athleteCountWidth) {
        isAthleteContainerVisible = false;
    }

    // 2nd level: Shorten the name with an ellipsis
    if (barWidth < textWidth + LABEL_PADDING) {
        const adjustedText = adjustLabelText(barWidth, textWidth, name, 8);
        const adjustedTextWidth = getStringWidth(adjustedText, LABEL_FONT_SIZE);
        truncatedString = adjustedText;
        textWidth = adjustedTextWidth;
    }

    // 3rd level: Remove the name
    if (barWidth < LABEL_PADDING) {
        truncatedString = '';
    }

    return { truncatedString, isAthleteContainerVisible };
};

export const getPointData = (sortedPeriods: Highcharts.GanttPointOptionsObject[], index: number) => {
    const { start, end, name, y, custom: { athleteCount = 0, periodId = '' } = {} } = sortedPeriods[index];
    return { start, end, name, y, athleteCount, periodId };
};

export const findActiveSide = (
    chart: Highcharts.Chart,
    xAxis: number,
    start: number,
    end: number,
): TimelineMouseState['activeSide'] => {
    const distToStart = Math.abs(start - xAxis);
    const distToEnd = Math.abs(end - xAxis);
    const chartRange = (chart?.xAxis[0]?.max || 0) - (chart?.xAxis[0]?.min || 0);
    if (distToStart <= chartRange * PERIOD_BAR_BUFFER) {
        return 'start';
    }
    if (distToEnd <= chartRange * PERIOD_BAR_BUFFER) {
        return 'end';
    }
    return undefined;
};

/**
 * Highcharts has warning when rendering HTML attributes that are not supported.
 * This function removes the unsupported attributes from the HTML string.
 * @param element
 * @returns
 */
export const getHighchartsHtmlString = (element: ReactElement): string => {
    let htmlString = renderToStaticMarkup(element);
    HIGHCHARTS_UNSUPPORTED_HTML_KEYS.forEach((key) => {
        htmlString = htmlString.replace(new RegExp(`${key}="[^"]*"\s*`, 'gi'), '');
    });
    return htmlString.replace(/\s+/g, ' ').trim();
};

export const updateActiveSideDateTime = (
    activeSide: TimelineMouseState['activeSide'],
    pointStart: number,
    pointEnd: number,
) => {
    const periodInformation: { start?: Date; end?: Date } = {};

    if (activeSide === 'start' || activeSide === 'center') {
        const newStartDateTime = new Date(pointStart);
        periodInformation.start = newStartDateTime;
    }

    if (activeSide === 'end' || activeSide === 'center') {
        const newEndDateTime = new Date(pointEnd);
        periodInformation.end = newEndDateTime;
    }

    return periodInformation;
};
